import React, { Fragment } from 'react'
import { string, object, number, bool, array, func } from 'prop-types'
import classnames from 'classnames'
import Loader from '../ui/loader/Loader'
import { sendClickEventToInsights } from '../../utils/insights'
import { getInsightsObject, appendAltToImages, hideAds } from '../../helpers/common'
import {
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ZERO,
  PRODUCTS,
  PROJECTS,
  IMAGES,
  IS_EXPLORER
} from '../../utils/constants'
import { ProjectGrid, ProductGrid, ArticleGrid, CompanyGrid, ImageGrid } from '../search-objects/gridview'
import { Article, Project, Product, Company, Widget, Professional } from '../search-objects'
import FoldersWidget from '../search-objects/folders/Widget'
import MoreSearchResults from './MoreResultsContainer'
import ResultsEmptyState from './EmptyState'
import SponsoredContent from '../search-objects/gridview/SponsoredContent'

const tokenFromUrl = url => {
  const match = (url || '').match(/\/(?:[a-z]{2}\/)?([0-9]+)(?:[-/?#]|$)/)
  return match ? match[1] : ''
}

const ResultsContainer = ({
  widgets = EMPTY_OBJECT,
  query = '',
  handleFolderWidgetError,
  site,
  scope,
  aggregationActive,
  gridView,
  lastQueryUsed,
  totalCount = ZERO,
  results = EMPTY_ARRAY,
  widgetsAreLoading = false,
  history,
  isLoading = false,
  offices = EMPTY_ARRAY,
  isGridAble,
  clearFilters,
  ...rest
}) => {
  const renderFoldersWidget = () => {
    const { folders } = widgets
    return (
      folders && <FoldersWidget
        items={folders.results}
        query={query}
        site={site}
        handleRequestError={handleFolderWidgetError}
        url={folders.view_more}
      />
    )
  }

  const renderResultsItem = (item, index) => {
    const { images, insights, categoriesItems } = getResultsItemData(item, index)
    const commonProps = { title: item.title, url: item.url, key: index, insights, type: aggregationActive }
    switch (item.document_type) {
      case 'products':
        return (
          <Product
            bimsFilesCount={item.bim_files_count || ZERO}
            caracteristics={item.meta_description}
            categories={categoriesItems}
            images={images}
            manufacturer={item.company}
            bim={item.bim}
            country={item.country || ''}
            gridView={gridView}
            duplicates={item.duplicates}
            scope={scope}
            {...commonProps}
          />
        )
      case 'projects':
        return (
          <Project
            architects={item.offices}
            categories={categoriesItems}
            companies={item.company}
            images={images}
            location={item.location || ''}
            gridView={gridView}
            publicationDate={item.publication_date}
            {...commonProps}
          />
        )
      case 'companies':
        return (
          <Company
            bimFilesCount={item.bim_files_count || ZERO}
            categories={categoriesItems}
            image={item.featured_images}
            linkedProjectsCount={item.linked_projects_count || ZERO}
            products={item.products}
            gridView={gridView}
            description={item.meta_description}
            {...commonProps}
          />
        )
      case 'experts': return <Professional {...commonProps} {...item} scope={scope} />
      default:
        return (
          <Article
            image={item.featured_images}
            preview={item.meta_description}
            publicationDate={item.publication_date}
            tags={item.tags}
            gridView={gridView}
            docType={item.document_type}
            {...commonProps}
          />
        )
    }
  }

  const renderResultsItemsGrid = (item, index, isBig = false) => {
    const { insights, categoriesItems } = getResultsItemData(item, index)
    const imagesUrls = item.miniatures || item.featured_images ? [item.featured_images, ...item.miniatures] : []
    const images = item.document_type === PROJECTS ? appendAltToImages(imagesUrls, item.images_alt) : imagesUrls
    const handleLinkClick = () => {
      sendClickEventToInsights(insights.category, insights.label, insights.value, insights.version)
    }
    /* eslint-disable-next-line max-len */
    const classes = classnames({ 'gridview__item--big': isBig })
    const commonProps = {
      handleLinkClick,
      insights,
      index,
      key: index,
      item,
      isBig,
      classes,
      type: aggregationActive,
      gridView
    }

    if (aggregationActive === IMAGES) {
      const insightsObject = getInsightsObject(IMAGES, item.parent_info.id, index, true, IMAGES, query)
      return <ImageGrid {...commonProps} insights={insightsObject} />
    }

    switch (item.document_type) {
      case 'products':
        return (
          <ProductGrid
            {...commonProps}
            isProductsView={aggregationActive === PRODUCTS}
            images={images}
            scope={scope}
          />
        )
      case 'companies': return <CompanyGrid {...commonProps} images={images} />
      case 'projects': return <ProjectGrid {...commonProps} images={images} categories={categoriesItems} />
      case 'sponsored_content': return <SponsoredContent {...item} {...commonProps} />
      default: return <ArticleGrid {...commonProps} />
    }
  }

  const getResultsItemData = (item, index) => {
    const imagesUrls = item.miniatures ? [ ...item.miniatures ] : []
    imagesUrls.unshift(item.featured_images)
    const images = item.document_type === PROJECTS ? appendAltToImages(imagesUrls, item.images_alt) : imagesUrls
    let categoriesItems = item.categories?.length ? item.categories.slice(0, 3) : []
    categoriesItems = item.tags ? categoriesItems.concat(item.tags.slice(0, 3)) : []
    const itemId = tokenFromUrl(item.url) || item.document_id || item.slug || item.title
    const insights = getInsightsObject(item.document_type, itemId, index, gridView, aggregationActive, query)
    return { images, insights, categoriesItems }
  }

  const renderWidget = index => {
    const { products, folders, articles, projects } = widgets
    const productsIsValid = products && products.results.length > 2
    const foldersIsValid = folders && folders.results.length > 2
    const articlesIsValid = articles && articles.results.length > 2
    const projectsIsValid = projects && projects.results.length > 2

    let type
    switch (index) {
      case 'first':
        if (productsIsValid) {
          type = 'products'
        } else if (foldersIsValid) {
          type = 'folders'
        } else if (articlesIsValid) {
          type = 'articles'
        } else if (projectsIsValid) {
          type = 'projects'
        }
        break
      case 'second':
        if (productsIsValid) {
          if (foldersIsValid) {
            type = 'folders'
          } else if (articlesIsValid) {
            type = 'articles'
          } else if (projectsIsValid) {
            type = 'projects'
          }
        } else if (foldersIsValid) {
          if (articlesIsValid) {
            type = 'articles'
          } else if (projectsIsValid) {
            type = 'projects'
          }
        } else if (articlesIsValid && projectsIsValid) {
          type = 'projects'
        }
        break
      case 'third':
        if (productsIsValid && foldersIsValid && articlesIsValid) {
          type = 'articles'
        } else if (projectsIsValid) {
          const widgetsOrdering = ['products', 'folders', 'articles', 'projects']
          let fails = ZERO
          widgetsOrdering.forEach((item, index) => {
            if (widgets[item]) {
              if (!(widgets[item].results.length > 2)) {
                fails++
              }
            }
          })
          if (fails === 1) {
            type = 'projects'
          }
        }
        break
      case 'fourth':
        if (productsIsValid && foldersIsValid && articlesIsValid && projectsIsValid) {
          type = 'projects'
        }
    }
    if (type === 'folders') {
      return renderFoldersWidget()
    }
    return (
      type && (
        <Widget
          items={widgets[type].results}
          type={type}
          query={query}
          url={widgets[type].view_more}
        />
      )
    )
  }

  const renderResults = () => {
    const commonProps = {
      searchType: aggregationActive,
      site,
      scope,
      query,
      history,
      location,
      totalCount,
      results,
      widgetsAreLoading,
      isGridAble,
      renderWidget,
      ...rest
    }
    // Key to force component rerender if products widget change
    const key = (!widgetsAreLoading && widgets['products']?.results?.[0]?.['document_id']) || 'empty'
    return (
      <Fragment>
        {gridView && !IS_EXPLORER && isGridAble
          ? (
            <div className={`gridview ${aggregationActive === IMAGES ? 'gridview--images' : ''}`}>
              <MoreSearchResults
                {...commonProps}
                offices={offices}
                gridView={gridView}
                renderResultsItem={renderResultsItemsGrid}
                widgets={widgets}
                key={key}
              />
            </div>
          )
          : (
            <MoreSearchResults
              {...commonProps}
              renderResultsItem={renderResultsItem}
              widgets={widgets}
              offices={offices}
            />
          )
        }
      </Fragment>
    )
  }

  const renderEmptyState = () => (
    <ResultsEmptyState query={lastQueryUsed} searchType={aggregationActive} site={site} clearFilters={clearFilters} />
  )

  const renderContent = () => {
    if (!isLoading) {
      if (totalCount > ZERO) {
        return renderResults()
      }
      hideAds()
      return renderEmptyState()
    }
  }
  return <div>{isLoading && <Loader fullScreen />}{renderContent()}</div>
}

ResultsContainer.propTypes = {
  totalCount: number.isRequired,
  results: array.isRequired,
  isLoading: bool,
  gridView: bool,
  query: string.isRequired,
  widgets: object,
  widgetsAreLoading: bool,
  isGridAble: bool,
  site: string,
  scope: string,
  aggregationActive: string,
  handleFolderWidgetError: func,
  clearFilters: func,
  history: object,
  lastQueryUsed: string,
  offices: array
}

export default ResultsContainer
